import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Layers, CalendarPlus, AlertTriangle, RotateCcw } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const JobSummary = ({ job }) => (
  <div data-testid="add-visit-job-summary" className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-3 space-y-1 text-xs">
    <p className="text-white font-bold text-sm">{job.customer_name} <span className="font-mono text-[#D4AF37] ml-1">{job.job_number}</span></p>
    <p className="text-[#D4D4D4]">{[job.department_name || 'No department', job.service_type].filter(Boolean).join(' → ')}</p>
    {job.address && <p className="text-[#A3A3A3]">{job.address}</p>}
    {(job.work_order_number || job.payor_type) && <p className="text-[#A3A3A3]">{job.work_order_number ? `WO/Claim ${job.work_order_number}` : ''}{job.work_order_number && job.payor_type ? ' · ' : ''}{job.payor_type ? `Payer: ${job.payor_type}` : ''}</p>}
    <div className="pt-1 space-y-0.5">
      {job.visits.map((v) => (
        <p key={v.id} className="text-[#A3A3A3]" data-testid={`add-visit-existing-${v.id}`}>
          Visit {v.visit_number}: {v.date} {v.start_time}–{v.end_time} · {v.employee_name || 'Unassigned'} · <span className="uppercase text-[10px]">{v.status}</span>
          {v.visit_outcome === 'return_visit' && <span className="ml-1 text-[10px] font-bold text-sky-300 uppercase">Return needed{v.return_visit?.reason_label ? ` — ${v.return_visit.reason_label}` : ''}</span>}
        </p>
      ))}
    </div>
    <p className="text-[10px] text-[#7f7f7f] pt-1">The new visit inherits customer, department, service type, WO/claim, payer and PM billing. It starts fresh — no claim, travel, completion, payment or invoice state is copied.</p>
  </div>
);

// iter316 — Return Visit context shown when scheduling from a Needs Scheduling action.
const ReturnActionSummary = ({ ra }) => (
  <div data-testid="add-visit-return-summary" className="rounded-md border border-sky-500/40 bg-sky-500/5 p-3 space-y-1 text-xs">
    <p className="text-sky-200 font-bold flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Return Visit — {ra.reason_label}</p>
    {ra.note && <p className="text-[#D4D4D4]">{ra.note}</p>}
    <p className="text-[#A3A3A3]">Last visit {ra.last_visit_date || '—'} · {ra.last_technician_name || '—'}</p>
    {ra.suggested_label && <p className="text-[#A3A3A3]" data-testid="add-visit-suggested">Suggested by tech: {ra.suggested_label}</p>}
    {ra.customer_promised && <p className="text-amber-300 font-bold" data-testid="add-visit-promised">CUSTOMER PROMISED — {ra.promised_label}</p>}
  </div>
);

const WINDOW_TIMES = { am: ['08:00', '12:00'], pm: ['12:00', '16:00'], all_day: ['08:00', '16:00'] };
const slotTimes = (slot) => {
  if (!slot) return ['09:00', '12:00'];
  if (slot.window === 'specific' && slot.time) {
    const [h, m] = slot.time.split(':').map(Number);
    return [slot.time, `${String(Math.min(h + 2, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`];
  }
  return WINDOW_TIMES[slot.window] || ['09:00', '12:00'];
};

// iter314 — "Add Visit to Existing Job". preset = { jobId, returnAction? } | { appointmentId } | null.
export const AddVisitSheet = ({ open, onOpenChange, employees, defaultDate, preset, onDone }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ jobs: [], unlinked_appointments: [] });
  const [job, setJob] = useState(null);
  const [unlinked, setUnlinked] = useState(null);
  const [form, setForm] = useState({ date: '', start_time: '09:00', end_time: '12:00', employee_id: '', notes: '', dispatcher_notes: '' });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);   // iter316 — promised-date soft warning
  const ra = preset?.returnAction || null;
  useEffect(() => { setConflict(null); }, [form.date, form.start_time, form.end_time]);

  useEffect(() => {
    if (!open) return;
    setJob(null); setUnlinked(null); setQ(''); setConflict(null);
    const [st, et] = slotTimes(preset?.returnAction?.promised || preset?.returnAction?.suggested);
    setForm({ date: defaultDate || '', start_time: st, end_time: et, employee_id: '', notes: preset?.returnAction ? `Return visit — ${preset.returnAction.reason_label}${preset.returnAction.note ? `: ${preset.returnAction.note}` : ''}` : '', dispatcher_notes: '' });
    if (preset?.jobId) axios.get(`${API}/admin/jobs/${preset.jobId}`, opts).then((r) => setJob(r.data)).catch(() => toast.error('Job not found'));
    if (preset?.appointmentId) setUnlinked(preset);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || job || unlinked) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API}/admin/jobs`, { ...opts, params: { q, status: 'open', include_unlinked: true, limit: 15 } })
        .then((r) => setResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, job, unlinked]);

  const submit = async (ack = false) => {
    if (!form.date || !form.start_time || !form.end_time) { toast.error('Date, start and end time are required'); return; }
    setSaving(true);
    try {
      let jobId = job?.id;
      if (!jobId && unlinked) {
        if (!window.confirm('This appointment was scheduled before Jobs existed. Create a Job record for it so visits can be grouped? (Only this appointment is linked; nothing else changes.)')) { setSaving(false); return; }
        jobId = (await axios.post(`${API}/admin/appointments/${unlinked.appointmentId}/start-job`, { confirm: true }, opts)).data.id;
      }
      const body = { ...form, employee_id: form.employee_id || null, acknowledge_promise_conflict: ack };
      if (ra) body.return_action_id = ra.id;
      const { data } = await axios.post(`${API}/admin/jobs/${jobId}/visits`, body, opts);
      toast.success(ra ? `Return visit scheduled — Visit ${data.appointment.visit_number} on ${data.job.job_number}` : `Visit ${data.appointment.visit_number} added to ${data.job.job_number}`);
      setConflict(null);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      // Global axios safety net flattens object details to a string; the original lives in _detailMeta.
      const meta = e.response?.data?._detailMeta || d;
      if (e.response?.status === 409 && meta?.code === 'PROMISE_CONFLICT') setConflict(meta.message);
      else toast.error((typeof d === 'string' && d) || 'Failed to add visit');
    }
    setSaving(false);
  };

  const picked = job || unlinked;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="add-visit-sheet" className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2"><Layers className="w-5 h-5 text-[#D4AF37]" /> {ra ? 'Add Return Visit' : 'Add Visit to Existing Job'}</SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">Schedule another trip for the same job — e.g. inspect Tuesday, continue Wednesday, finish Thursday. One job, one billing relationship.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          {!picked && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-[#525252] absolute left-2.5 top-3" />
                <Input data-testid="add-visit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, job #, WO/claim #, address…" className="pl-8 bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="add-visit-results">
                {results.jobs.map((j) => (
                  <button key={j.id} data-testid={`add-visit-job-${j.id}`} onClick={() => setJob(j)} className="w-full text-left rounded border border-[#262626] bg-[#0A0A0A] hover:border-[#D4AF37]/60 p-2">
                    <p className="text-white text-sm font-semibold">{j.customer_name} <span className="font-mono text-[#D4AF37] text-xs">{j.job_number}</span></p>
                    <p className="text-[11px] text-[#A3A3A3]">{[j.department_name, j.service_type].filter(Boolean).join(' → ')} · {j.visit_count} visit(s) · last {j.last_visit_date || '—'}</p>
                  </button>
                ))}
                {results.unlinked_appointments.length > 0 && <p className="text-[10px] uppercase tracking-wider text-[#525252] pt-2">Earlier appointments (no Job record yet)</p>}
                {results.unlinked_appointments.map((a) => (
                  <button key={a.appointment_id} data-testid={`add-visit-unlinked-${a.appointment_id}`} onClick={() => setUnlinked({ appointmentId: a.appointment_id, ...a })} className="w-full text-left rounded border border-dashed border-[#3f3f3f] bg-[#0A0A0A] hover:border-[#D4AF37]/60 p-2">
                    <p className="text-white text-sm">{a.customer_name}</p>
                    <p className="text-[11px] text-[#A3A3A3]">{a.service_type} · {a.date} · {a.employee_name || 'Unassigned'} · <span className="uppercase">{a.status}</span></p>
                  </button>
                ))}
                {!results.jobs.length && !results.unlinked_appointments.length && <p className="text-xs text-[#525252] py-4 text-center" data-testid="add-visit-no-results">No open jobs found.</p>}
              </div>
            </div>
          )}
          {ra && <ReturnActionSummary ra={ra} />}
          {job && <JobSummary job={job} />}
          {!job && unlinked && (
            <div data-testid="add-visit-unlinked-summary" className="rounded-md border border-dashed border-[#D4AF37]/50 p-3 text-xs space-y-1">
              <p className="text-white font-bold text-sm">{unlinked.customer_name}</p>
              <p className="text-[#A3A3A3]">{unlinked.service_type} · {unlinked.date}</p>
              <p className="text-orange-300">No Job record yet — saving will create one for this appointment (Visit 1) and add the new visit as Visit 2.</p>
            </div>
          )}
          {picked && (
            <>
              {!preset && <button data-testid="add-visit-change-job" onClick={() => { setJob(null); setUnlinked(null); }} className="text-[11px] text-[#D4AF37] hover:underline">← Pick a different job</button>}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3"><Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Visit Date</Label><Input data-testid="add-visit-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                <div className="col-span-1"><Label className="text-xs text-[#A3A3A3]">Start</Label><Input data-testid="add-visit-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                <div className="col-span-1"><Label className="text-xs text-[#A3A3A3]">End</Label><Input data-testid="add-visit-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Technician</Label>
                <select data-testid="add-visit-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
                  <option value="">— Unassigned (claimable) —</option>
                  {(employees || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Visit Notes (tech-facing)</Label><Textarea data-testid="add-visit-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Return to finish repipe in basement" className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Dispatcher Notes (internal)</Label><Textarea data-testid="add-visit-dispatcher-notes" rows={2} value={form.dispatcher_notes} onChange={(e) => setForm({ ...form, dispatcher_notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              {conflict && (
                <div data-testid="add-visit-promise-conflict" className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs space-y-2">
                  <p className="text-amber-200 font-bold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Outside the customer promise</p>
                  <p className="text-amber-100" data-testid="add-visit-promise-conflict-msg">{conflict}</p>
                  <div className="flex gap-2">
                    <Button data-testid="add-visit-conflict-continue" size="sm" disabled={saving} onClick={() => submit(true)} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Continue Anyway</Button>
                    <Button data-testid="add-visit-conflict-cancel" size="sm" variant="outline" onClick={() => setConflict(null)} className="border-[#262626] text-white">Change Date/Time</Button>
                  </div>
                </div>
              )}
              <Button data-testid="add-visit-submit" disabled={saving || !!conflict} onClick={() => submit(false)} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"><CalendarPlus className="w-4 h-4 mr-1" />{saving ? 'Adding…' : (ra ? 'Add Return Visit' : 'Add Visit')}</Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddVisitSheet;
